import { useEffect, useRef, useCallback } from 'react'
import type { MapRef } from 'react-map-gl'
import type { GeoJSONSource } from 'mapbox-gl'
import type MapboxDraw from '@mapbox/mapbox-gl-draw'
import type { Zone } from '../../../../zones/types'

interface UseMapNativeEventsParams {
  readonly mapRef: React.MutableRefObject<MapRef | null>
  readonly drawRef: React.MutableRefObject<MapboxDraw | null>
  readonly drawingMode: 'draw' | 'grid' | 'view'
  readonly editingZoneId?: string | null
  readonly editMode?: 'polygon' | 'grid' | null
  readonly gridCellLayerId: string
  readonly gridCellSourceId: string
  readonly onGridCellPositionChange?: (
    position: [number, number] | null
  ) => void
  readonly gridSize: number
  readonly hasPolygon: boolean
  readonly gridCellPosition: [number, number] | null | undefined
  readonly generateGridCell: (
    center: [number, number],
    size: number
  ) => GeoJSON.Feature
  readonly setDragPanEnabled: React.Dispatch<React.SetStateAction<boolean>>
  readonly dragPanEnabled: boolean
  readonly getZoneById: (id: string) =>
    | {
        coordinates?: number[][]
      }
    | undefined
  readonly handleSetSelectedZone: (zone: Zone | null) => void
  readonly zonesLayerId: string
  readonly justFinishedDraggingRef?: React.MutableRefObject<boolean>
  readonly onError?: (title: string, message: string) => void
  readonly blockMapInteractionsRef?: React.MutableRefObject<{
    blocked: boolean
    x: number
    y: number
    time: number
  }>
}

export const useMapNativeEvents = (params: UseMapNativeEventsParams) => {
  const {
    mapRef,
    drawingMode,
    editingZoneId,
    editMode,
    gridCellLayerId,
    gridCellSourceId,
    onGridCellPositionChange,
    gridSize,
    generateGridCell,
    setDragPanEnabled,
    getZoneById,
    handleSetSelectedZone,
    zonesLayerId,
    justFinishedDraggingRef,
    blockMapInteractionsRef,
  } = params

  const localJustFinishedRef = useRef(false)
  const isDraggingGridCellRef = useRef(false)
  const dragStartedAtRef = useRef<number | null>(null)
  const pendingGridCellDragRef = useRef(false)
  const dragStartPointPxRef = useRef<[number, number] | null>(null)
  const dragStartPointRef = useRef<[number, number] | null>(null)

  const isGhostInteraction = useCallback(
    (x: number, y: number) => {
      if (!blockMapInteractionsRef?.current) return false
      const { x: blockX, y: blockY, time } = blockMapInteractionsRef.current
      const now = Date.now()
      if (now - time < 500) {
        const dist = Math.sqrt(
          Math.pow(x - blockX, 2) + Math.pow(y - blockY, 2)
        )
        return dist < 60
      }
      return false
    },
    [blockMapInteractionsRef]
  )

  const getEventPoint = useCallback(
    (
      event: { clientX: number; clientY: number },
      canvas: HTMLElement
    ): [number, number] => {
      const bbox = canvas.getBoundingClientRect()
      return [event.clientX - bbox.left, event.clientY - bbox.top]
    },
    []
  )

  const getTouchPoint = useCallback(
    (event: TouchEvent, canvas: HTMLElement): [number, number] | null => {
      const touch = event.touches[0] ?? event.changedTouches[0]
      if (!touch) return null
      return getEventPoint(touch, canvas)
    },
    [getEventPoint]
  )

  const isValidCanvasTarget = useCallback(
    (target: EventTarget | null, canvas: HTMLElement) => {
      return target === canvas || canvas.contains(target as Node)
    },
    []
  )

  const startDrag = useCallback(
    (map: mapboxgl.Map, point: [number, number]) => {
      if (map.dragPan) map.dragPan.disable()
      setDragPanEnabled(false)
      isDraggingGridCellRef.current = true
      dragStartedAtRef.current = Date.now()
      const lngLat = map.unproject(point)
      dragStartPointRef.current = [lngLat.lng, lngLat.lat]
      map.getCanvas().style.cursor = 'move'
    },
    [setDragPanEnabled]
  )

  const endDrag = useCallback(
    (map: mapboxgl.Map) => {
      isDraggingGridCellRef.current = false
      dragStartPointRef.current = null
      dragStartPointPxRef.current = null
      dragStartedAtRef.current = null
      map.getCanvas().style.cursor = ''
      setDragPanEnabled(true)
      if (map.dragPan) map.dragPan.enable()
      const justFinishedDragging =
        justFinishedDraggingRef ?? localJustFinishedRef
      justFinishedDragging.current = true
      setTimeout(() => {
        justFinishedDragging.current = false
      }, 100)
    },
    [setDragPanEnabled, justFinishedDraggingRef]
  )

  const updateGridSource = useCallback(
    (map: mapboxgl.Map, newPosition: [number, number]) => {
      try {
        const source = map.getSource(gridCellSourceId) as GeoJSONSource
        if (source) {
          const gridCell = generateGridCell(newPosition, gridSize)
          source.setData({ type: 'FeatureCollection', features: [gridCell] })
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating grid cell source:', error)
      }
    },
    [gridCellSourceId, generateGridCell, gridSize]
  )

  const updateGridCellPosition = useCallback(
    (map: mapboxgl.Map, newPosition: [number, number]) => {
      if (!editingZoneId && drawingMode === 'grid') {
        updateGridSource(map, newPosition)
      }
      onGridCellPositionChange?.(newPosition)
    },
    [editingZoneId, drawingMode, updateGridSource, onGridCellPositionChange]
  )

  const createGridCellOnTap = useCallback(
    (map: mapboxgl.Map, point: [number, number]) => {
      if (!onGridCellPositionChange) return

      let zoneFeatures: GeoJSON.Feature[] = []
      try {
        zoneFeatures = map.queryRenderedFeatures(point, {
          layers: [zonesLayerId],
        })
      } catch {
        /* ignore */
      }

      let gridCellFeatures: GeoJSON.Feature[] = []
      if (map.getLayer(gridCellLayerId)) {
        try {
          gridCellFeatures = map.queryRenderedFeatures(point, {
            layers: [gridCellLayerId],
          })
        } catch {
          /* ignore */
        }
      }

      if (zoneFeatures.length === 0 && gridCellFeatures.length === 0) {
        const lngLat = map.unproject(point)
        const clickPosition: [number, number] = [lngLat.lng, lngLat.lat]
        const currentZoomLevel = map.getZoom()
        const is100mGrid = gridSize === 0.1
        const minZoom = 14

        if (is100mGrid && currentZoomLevel < minZoom) {
          map.once('moveend', () => {
            if (mapRef.current) onGridCellPositionChange(clickPosition)
          })
          map.flyTo({ center: clickPosition, zoom: minZoom, duration: 500 })
        } else {
          onGridCellPositionChange(clickPosition)
        }
      }
    },
    [onGridCellPositionChange, zonesLayerId, gridCellLayerId, gridSize, mapRef]
  )

  const selectZoneOnTap = useCallback(
    (
      map: mapboxgl.Map,
      point: [number, number],
      eventTarget: EventTarget | null
    ) => {
      const canvas = map.getCanvasContainer()
      if (!isValidCanvasTarget(eventTarget, canvas)) return

      try {
        const zoneFeatures = map.queryRenderedFeatures(point, {
          layers: [zonesLayerId],
        })
        if (zoneFeatures.length > 0) {
          const feature = zoneFeatures[0]
          if (feature?.properties) {
            const zoneId = feature.properties['zoneId'] as string | undefined
            if (zoneId) {
              const zone = getZoneById(zoneId)
              if (zone) {
                handleSetSelectedZone(zone as Zone)
                return
              }
            }
          }
        }
      } catch {
        /* ignore */
      }
    },
    [zonesLayerId, getZoneById, handleSetSelectedZone, isValidCanvasTarget]
  )

  const checkGridCellDragStart = useCallback(
    (map: mapboxgl.Map, point: [number, number]) => {
      if (!map.getLayer(gridCellLayerId)) return false
      try {
        const features = map.queryRenderedFeatures(point, {
          layers: [gridCellLayerId],
        })
        if (features.length > 0) {
          pendingGridCellDragRef.current = true
          dragStartPointPxRef.current = point
          return true
        }
      } catch {
        /* ignore */
      }
      return false
    },
    [gridCellLayerId]
  )

  const isZoomInvalidFor100mGrid = useCallback(
    (map: mapboxgl.Map) => {
      if (editingZoneId) return false
      if (gridSize !== 0.1) return false
      const zoom = map.getZoom()
      return zoom < 14 || zoom > 15
    },
    [editingZoneId, gridSize]
  )

  const handleMouseDown = useCallback(
    (map: mapboxgl.Map, canvas: HTMLElement) => (event: MouseEvent) => {
      if (isGhostInteraction(event.clientX, event.clientY)) return
      if (event.button !== 0 || !isValidCanvasTarget(event.target, canvas))
        return

      const point = getEventPoint(event, canvas)
      if (checkGridCellDragStart(map, point)) return

      if (editingZoneId && editMode !== 'grid') return
      if (isZoomInvalidFor100mGrid(map)) return
    },
    [
      isGhostInteraction,
      isValidCanvasTarget,
      getEventPoint,
      checkGridCellDragStart,
      editingZoneId,
      editMode,
      isZoomInvalidFor100mGrid,
    ]
  )

  const updateCursorStyle = useCallback(
    (map: mapboxgl.Map, point: [number, number]) => {
      const isGridMode =
        drawingMode === 'grid' || (editingZoneId && editMode === 'grid')
      if (!isGridMode || !map.getLayer(gridCellLayerId)) {
        return
      }

      try {
        const features = map.queryRenderedFeatures(point, {
          layers: [gridCellLayerId],
        })
        map.getCanvas().style.cursor = features.length > 0 ? 'move' : ''
      } catch {
        /* ignore */
      }
    },
    [drawingMode, editingZoneId, editMode, gridCellLayerId]
  )

  const handleMouseMove = useCallback(
    (map: mapboxgl.Map, canvas: HTMLElement) => (event: MouseEvent) => {
      const point = getEventPoint(event, canvas)

      // 1. Handle Active Drag
      if (isDraggingGridCellRef.current) {
        const lngLat = map.unproject(point)
        updateGridCellPosition(map, [lngLat.lng, lngLat.lat])
        map.getCanvas().style.cursor = 'move'
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        return
      }

      // 2. Handle Pending Drag Start
      const dragStart = dragStartPointPxRef.current
      if (pendingGridCellDragRef.current && dragStart) {
        if (Math.hypot(point[0] - dragStart[0], point[1] - dragStart[1]) >= 3) {
          pendingGridCellDragRef.current = false
          dragStartPointPxRef.current = null
          startDrag(map, point)
          event.preventDefault()
          event.stopPropagation()
          event.stopImmediatePropagation()
          return
        }
      }

      // 3. Update cursor style in grid mode
      updateCursorStyle(map, point)
    },
    [getEventPoint, startDrag, updateGridCellPosition, updateCursorStyle]
  )

  const handleMouseUp = useCallback(
    (map: mapboxgl.Map) => (event: MouseEvent) => {
      const wasDragging = dragStartedAtRef.current !== null

      if (pendingGridCellDragRef.current && !isDraggingGridCellRef.current) {
        pendingGridCellDragRef.current = false
        dragStartPointPxRef.current = null
      }

      if (wasDragging) {
        endDrag(map)
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
      }
    },
    [endDrag]
  )

  const handleTouchStart = useCallback(
    (map: mapboxgl.Map, canvas: HTMLElement) => (event: TouchEvent) => {
      const touch = event.touches[0] ?? event.changedTouches[0]
      if (touch && isGhostInteraction(touch.clientX, touch.clientY)) return

      const point = getTouchPoint(event, canvas)
      if (!point) return

      if (checkGridCellDragStart(map, point)) return

      if (editingZoneId && editMode !== 'grid') return
    },
    [
      isGhostInteraction,
      getTouchPoint,
      checkGridCellDragStart,
      editingZoneId,
      editMode,
    ]
  )

  const handleTouchMove = useCallback(
    (map: mapboxgl.Map, canvas: HTMLElement) => (event: TouchEvent) => {
      const point = getTouchPoint(event, canvas)
      if (!point) return

      if (
        pendingGridCellDragRef.current &&
        !isDraggingGridCellRef.current &&
        dragStartPointPxRef.current
      ) {
        const moveThresholdPx = 6
        const [startX, startY] = dragStartPointPxRef.current
        const dx = point[0] - startX
        const dy = point[1] - startY
        const distance = Math.sqrt(dx * dx + dy * dy)
        if (distance >= moveThresholdPx) {
          pendingGridCellDragRef.current = false
          dragStartPointPxRef.current = null
          startDrag(map, point)
          event.preventDefault()
          event.stopPropagation()
          event.stopImmediatePropagation()
          return
        }
      }

      if (isDraggingGridCellRef.current) {
        const lngLat = map.unproject(point)
        updateGridCellPosition(map, [lngLat.lng, lngLat.lat])
        map.getCanvas().style.cursor = 'move'
        event.preventDefault()
        event.stopPropagation()
      }
    },
    [getTouchPoint, startDrag, updateGridCellPosition]
  )

  const performTapAction = useCallback(
    (
      map: mapboxgl.Map,
      point: [number, number],
      target: EventTarget | null,
      canvas: HTMLElement
    ) => {
      if (drawingMode === 'grid') {
        if (isValidCanvasTarget(target, canvas)) {
          createGridCellOnTap(map, point)
        }
        return
      }
      selectZoneOnTap(map, point, target)
    },
    [drawingMode, isValidCanvasTarget, createGridCellOnTap, selectZoneOnTap]
  )

  const handleTouchEnd = useCallback(
    (map: mapboxgl.Map, canvas: HTMLElement) => (event: TouchEvent) => {
      const touch = event.changedTouches[0]
      if (!touch || isGhostInteraction(touch.clientX, touch.clientY)) return

      // 1. Reset pending drag if released before starting
      if (pendingGridCellDragRef.current && !isDraggingGridCellRef.current) {
        pendingGridCellDragRef.current = false
        dragStartPointPxRef.current = null
        return
      }

      // 2. End active drag
      if (dragStartedAtRef.current !== null) {
        endDrag(map)
        event.preventDefault()
        event.stopPropagation()
        return
      }

      // 3. Handle tap actions (Create or Select)
      const point = getTouchPoint(event, canvas)
      if (point && !editingZoneId) {
        performTapAction(map, point, event.target, canvas)
      }
    },
    [
      isGhostInteraction,
      endDrag,
      editingZoneId,
      getTouchPoint,
      performTapAction,
    ]
  )

  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (!map) return undefined

    const canvas = map.getCanvasContainer()

    const mousedown = handleMouseDown(map, canvas)
    const mousemove = handleMouseMove(map, canvas)
    const mouseup = handleMouseUp(map)
    const touchstart = handleTouchStart(map, canvas)
    const touchmove = handleTouchMove(map, canvas)
    const touchend = handleTouchEnd(map, canvas)

    canvas.addEventListener('mousedown', mousedown, true)
    canvas.addEventListener('mousemove', mousemove, true)
    canvas.addEventListener('mouseup', mouseup, true)
    canvas.addEventListener('touchstart', touchstart, {
      passive: false,
      capture: true,
    })
    canvas.addEventListener('touchmove', touchmove, {
      passive: false,
      capture: true,
    })
    canvas.addEventListener('touchend', touchend, true)
    window.addEventListener('mouseup', mouseup, true)
    window.addEventListener('touchend', touchend, true)

    return () => {
      canvas.removeEventListener('mousedown', mousedown, true)
      canvas.removeEventListener('mousemove', mousemove, true)
      canvas.removeEventListener('mouseup', mouseup, true)
      canvas.removeEventListener('touchstart', touchstart, true)
      canvas.removeEventListener('touchmove', touchmove, true)
      canvas.removeEventListener('touchend', touchend, true)
      window.removeEventListener('mouseup', mouseup, true)
      window.removeEventListener('touchend', touchend, true)
    }
  }, [
    mapRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  ])
}
